import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SimpleDonationButton from '../SimpleDonationButton';
import { DONATION_AMOUNTS } from '../../constants/donationAmounts';

/**
 * Unified Impact & Support Component
 * Combines impact statistics with donation CTAs in a compact mobile-friendly design
 */
const UnifiedImpactSupport = ({ config, stats, theme, isMobile }) => {
  const [showDonation, setShowDonation] = useState(false);

  // Get current year
  const currentYear = new Date().getFullYear();
  
  // Get program count from stats or use default
  const programCount = stats?.programs || 0;
  
  // Get annual projection data if available
  const projectionData = stats?.annualProjection;
  // Show projection whenever it exists (we're now using conservative estimates)
  const hasProjection = projectionData?.projection !== null && projectionData?.projection !== undefined;

  // Button styles for consistency
  const buttonBaseStyle = {
    padding: '12px 32px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textDecoration: 'none',
    display: 'inline-block',
    minWidth: isMobile ? '140px' : '180px',
    textAlign: 'center',
    border: 'none'
  };

  return (
    <div style={{
      marginTop: isMobile ? '30px' : '40px',
      marginBottom: isMobile ? '30px' : '40px',
      maxWidth: '800px',
      margin: `${isMobile ? '30px' : '40px'} auto`,
      padding: isMobile ? '24px' : '32px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Subtle background decoration */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: `${theme?.colors?.primary || '#1e4273'}10`,
        pointerEvents: 'none'
      }} />

      {/* Main Content */}
      <div style={{
        position: 'relative',
        textAlign: 'center'
      }}>
        {/* Impact Statement */}
        <h3 style={{
          fontSize: isMobile ? '18px' : '20px',
          fontWeight: '600',
          color: theme?.colors?.primary || '#1e4273',
          marginBottom: '16px',
          lineHeight: '1.4'
        }}>
          {hasProjection ? (
            <>
              With Allah's help and your support, {config?.name || 'our community'} plans to organize{' '}
              <span style={{
                color: theme?.colors?.accent || '#33C3F0',
                fontWeight: '700',
                fontSize: isMobile ? '20px' : '22px'
              }}>
                {projectionData.projection}
              </span>
              {' '}events in {currentYear}.
            </>
          ) : (
            <>
              With Allah's help and your support, {config?.name || 'our community'} organized{' '}
              <span style={{
                color: theme?.colors?.accent || '#33C3F0',
                fontWeight: '700',
                fontSize: isMobile ? '20px' : '22px'
              }}>
                {programCount}
              </span>
              {' '}programs in {currentYear} so far.
            </>
          )}
        </h3>

        {/* Mission Statement */}
        <p style={{
          fontSize: isMobile ? '14px' : '15px',
          color: '#6b7280',
          lineHeight: '1.6',
          marginBottom: '24px',
          maxWidth: '600px',
          margin: '0 auto 24px'
        }}>
          Our programs are designed to build a community ready for Imam Mehdi (AS). 
          Your support enables us to continue this sacred mission.
        </p>

        {/* Donation Component */}
        {showDonation && (
          <div style={{
            marginBottom: '24px',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={() => setShowDonation(false)}
              style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 10
              }}
            >
              ×
            </button>
            
            {/* SimpleDonationButton with standalone Stripe checkout */}
            <SimpleDonationButton
              announcement={{
                id: 'general-donation',
                title: `Support ${config?.name || 'Our Community'} Programs`,
                sponsor_enabled: true,
                suggested_amounts: DONATION_AMOUNTS.default,
                sponsor_message: 'Your donation helps us organize programs to build a community ready for Imam Mehdi (AS).'
              }}
              center={{
                code: config?.code || 'DEZ',
                name: config?.name || 'Our Community'
              }}
              userEmail={null}
            />
          </div>
        )}

        {/* CTA Buttons */}
        {!showDonation && (
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            {/* Ongoing Support Button - Now First */}
            <Link
              to="/support"
              style={{
                ...buttonBaseStyle,
                backgroundColor: theme?.colors?.primary || '#1e4273',
                color: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                flex: '1'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              Support Options
            </Link>

            {/* Quick Donate Button - Now Second */}
            <button
              onClick={() => setShowDonation(true)}
              style={{
                ...buttonBaseStyle,
                backgroundColor: 'transparent',
                color: theme?.colors?.primary || '#1e4273',
                border: `2px solid ${theme?.colors?.primary || '#1e4273'}`,
                flex: '1'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#1e4273';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = theme?.colors?.primary || '#1e4273';
              }}
            >
              Quick Donate
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedImpactSupport;